import React, { useState } from 'react';
import { X, Heart, Coffee, ExternalLink, Copy, Check, CreditCard, BookOpen, Share2 } from 'lucide-react';
import { Button } from './ui/button';

// Donation links are configured through environment variables (set them in
// Netlify → Site configuration → Environment variables, then redeploy). Any option left blank is
// simply not shown. See README → Donations for the step-by-step setup.
//
// Security: the gallery never sees or handles any payment details. Every button only opens the
// payment provider's own page (Buy Me a Coffee, Ko-fi, PayPal, Razorpay) or the visitor's UPI app,
// where the payment happens. To make sure a mistyped or tampered setting can never send visitors
// somewhere else, each link must be https:// and on that provider's own domain, and the UPI ID must
// look like a UPI ID; anything else is ignored (and reported in the browser console).
const PROVIDERS = [
  { key: 'bmc', label: 'Buy Me a Coffee', env: 'REACT_APP_DONATE_BUYMEACOFFEE_URL', url: process.env.REACT_APP_DONATE_BUYMEACOFFEE_URL,
    hosts: ['buymeacoffee.com', 'www.buymeacoffee.com'], className: 'bg-yellow-400 hover:bg-yellow-500 text-gray-900', Icon: Coffee },
  { key: 'kofi', label: 'Ko-fi', env: 'REACT_APP_DONATE_KOFI_URL', url: process.env.REACT_APP_DONATE_KOFI_URL,
    hosts: ['ko-fi.com', 'www.ko-fi.com'], className: 'bg-sky-500 hover:bg-sky-600 text-white', Icon: Coffee },
  { key: 'paypal', label: 'PayPal', env: 'REACT_APP_DONATE_PAYPAL_URL', url: process.env.REACT_APP_DONATE_PAYPAL_URL,
    hosts: ['paypal.me', 'www.paypal.me', 'paypal.com', 'www.paypal.com'], className: 'bg-blue-700 hover:bg-blue-800 text-white', Icon: Heart },
  { key: 'razorpay', label: 'Card / UPI / Netbanking (Razorpay)', env: 'REACT_APP_DONATE_RAZORPAY_URL', url: process.env.REACT_APP_DONATE_RAZORPAY_URL,
    hosts: ['rzp.io', 'razorpay.me', 'pages.razorpay.com'], className: 'bg-indigo-600 hover:bg-indigo-700 text-white', Icon: CreditCard },
];

export const safeDonationUrl = (value, hosts) => {
  if (!value) return null;
  try {
    const u = new URL(String(value).trim());
    if (u.protocol !== 'https:' || u.username || u.password || !hosts.includes(u.hostname.toLowerCase())) return null;
    return u.toString();
  } catch (e) {
    return null;
  }
};

// name@bank, e.g. davidrajpaul@okaxis
export const safeUpiId = (value) => {
  const v = String(value || '').trim();
  return /^[a-zA-Z0-9._-]{2,64}@[a-zA-Z]{2,32}$/.test(v) ? v : null;
};

export const donateOptions = PROVIDERS.map((p) => {
  const url = safeDonationUrl(p.url, p.hosts);
  if (p.url && !url && typeof console !== 'undefined') {
    console.warn(`${p.env} is not a valid https link on ${p.hosts[0]}; that donation option is hidden.`);
  }
  return { ...p, url };
}).filter((p) => p.url);

export const upiId = safeUpiId(process.env.REACT_APP_DONATE_UPI_ID);
if (process.env.REACT_APP_DONATE_UPI_ID && !upiId && typeof console !== 'undefined') {
  console.warn('REACT_APP_DONATE_UPI_ID does not look like a UPI ID (name@bank); UPI is hidden.');
}

export const isDonateEnabled = donateOptions.length > 0 || Boolean(upiId);

const ARTIST = 'David Raj Paul';
const INSTAGRAM_URL = 'https://www.instagram.com/davidraj.paul/';
const FACEBOOK_URL = 'https://www.facebook.com/artbydavidraj/';

const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
  </svg>
);
const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M14 8h3V4h-3c-2.8 0-4.5 1.8-4.5 4.6V11H7v4h2.5v9h4v-9h3l.5-4h-3.5V8.9c0-.6.4-.9 1-.9z" />
  </svg>
);

// Following is free and helps just as much: shown in every case.
const FollowLinks = () => (
  <div className="text-center">
    <p className="text-sm text-gray-700 mb-2">Follow the artist for new paintings and process videos:</p>
    <div className="grid grid-cols-2 gap-2">
      <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 rounded-lg px-3 py-2 font-semibold text-white bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 hover:opacity-90">
        <InstagramIcon /> Instagram
      </a>
      <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 rounded-lg px-3 py-2 font-semibold text-white bg-[#1877f2] hover:bg-[#166fe0]">
        <FacebookIcon /> Facebook
      </a>
    </div>
  </div>
);

const DonateModal = ({ isOpen, onClose, onOpenGuestbook }) => {
  const [copied, setCopied] = useState(false);
  if (!isOpen) return null;

  const upiLink = upiId
    ? `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(ARTIST)}&cu=INR&tn=${encodeURIComponent('Support for art')}`
    : null;

  const copyUpi = async () => {
    try { await navigator.clipboard.writeText(upiId); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { /* ignore */ }
  };

  const share = async () => {
    const url = window.location.origin;
    try {
      if (navigator.share) await navigator.share({ title: `${ARTIST}'s 3D Art Gallery`, url });
      else { await navigator.clipboard.writeText(url); }
    } catch (e) { /* cancelled */ }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Support the Artist"
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Heart size={20} className="text-red-500 fill-red-500" />
            Support the Artist
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <X size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-5">
          <p className="text-gray-700 leading-relaxed">
            Every painting in this gallery takes many hours of work. If you enjoyed walking through it,
            a small contribution helps buy paints, canvases and time to create more art and process videos.
          </p>

          {isDonateEnabled ? (
            <div className="space-y-3">
              {donateOptions.map(({ key, label, url, className, Icon }) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-2 w-full rounded-lg px-4 py-3 font-semibold transition-colors ${className}`}
                >
                  <Icon size={18} />
                  {label}
                  <ExternalLink size={14} className="opacity-70" />
                </a>
              ))}

              {upiLink && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
                  <p className="text-sm text-amber-800 mb-2">UPI (India): tap to open GPay / PhonePe / Paytm</p>
                  <a href={upiLink} className="font-mono font-semibold text-amber-900 underline break-all">
                    {upiId}
                  </a>
                  <button onClick={copyUpi} className="ml-2 inline-flex items-center gap-1 text-xs text-amber-800 hover:text-amber-950 align-middle">
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}

              <p className="text-[11px] text-center text-gray-500">
                Payments are made securely on the provider's own site or in your UPI app. This gallery never sees your
                card or bank details.
              </p>
            </div>
          ) : (
            // Nothing configured yet: still offer free ways to help.
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3">
              <p>Online donations are coming soon. Meanwhile, here are other ways to help:</p>
              <div className="flex flex-col gap-2">
                <button onClick={share} className="flex items-center justify-center gap-2 rounded-lg bg-white border border-amber-300 px-3 py-2 font-medium hover:bg-amber-100">
                  <Share2 size={16} /> Share the gallery with a friend
                </button>
                {onOpenGuestbook && (
                  <button onClick={onOpenGuestbook} className="flex items-center justify-center gap-2 rounded-lg bg-white border border-amber-300 px-3 py-2 font-medium hover:bg-amber-100">
                    <BookOpen size={16} /> Leave a note in the guestbook
                  </button>
                )}
              </div>
            </div>
          )}

          <FollowLinks />

          <p className="text-xs text-center text-gray-500">Thank you for supporting independent art ❤️</p>
        </div>
      </div>
    </div>
  );
};

export default DonateModal;
